import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CloudDownload, CloudUpload, RefreshCw, StopCircle, CheckCircle2, AlertTriangle, Link2, DownloadCloud } from "lucide-react";

function Stat({ label, value, tone = "slate" }) {
  const toneMap = {
    slate: "text-slate-900",
    green: "text-emerald-600",
    amber: "text-amber-600",
    rose: "text-rose-600",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`bp-stat-${label.toLowerCase().replace(/\s/g,"-")}`}>
      <div className="caption text-slate-500">{label}</div>
      <div className={`font-heading text-3xl font-extrabold mt-1 ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}

export default function BukkuProducts() {
  const [status, setStatus] = useState({ total_local_skus: 0, synced_to_bukku: 0, missing: 0 });
  const [conn, setConn] = useState(null);
  const [pullState, setPullState] = useState({ running: false, total: 0, done: 0, matched: 0, unmatched: 0, error: null });
  const [pushState, setPushState] = useState({ running: false, total: 0, done: 0, created: 0, failed: 0, errors: [] });
  const [importState, setImportState] = useState({ running: false, total: 0, done: 0, created: 0, updated: 0, skipped: 0, error: null });
  const [busy, setBusy] = useState(null);
  const pollRef = useRef(null);

  const loadStatus = async () => {
    const s = await api.get("/bukku/products/status"); setStatus(s.data);
  };
  const loadPushState = async () => {
    const s = await api.get("/bukku/products/push-status"); setPushState(s.data);
    return s.data;
  };
  const loadPullState = async () => {
    const s = await api.get("/bukku/products/pull-status"); setPullState(s.data);
    return s.data;
  };
  const loadImportState = async () => {
    const s = await api.get("/bukku/products/import-status"); setImportState(s.data);
    return s.data;
  };
  const testConn = async () => {
    try {
      const r = await api.get("/bukku/connection-test");
      setConn(r.data);
      if (r.data.ok) toast.success(`Connected · ${r.data.companies?.[0]?.legal_name || r.data.subdomain}`);
      else toast.error(r.data.error || "Bukku connection failed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    }
  };

  useEffect(() => {
    loadStatus(); testConn(); loadPushState(); loadPullState(); loadImportState();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const anyRunning = pushState.running || pullState.running || importState.running;
    if (anyRunning && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const p1 = pushState.running ? await loadPushState() : null;
        const p2 = pullState.running ? await loadPullState() : null;
        const p3 = importState.running ? await loadImportState() : null;
        const stillRunning = (p1 && p1.running) || (p2 && p2.running) || (p3 && p3.running);
        if (!stillRunning) {
          clearInterval(pollRef.current); pollRef.current = null;
          await loadStatus();
          if (p1) toast.success(`Push finished · ${p1.created} created · ${p1.failed} failed`);
          if (p2) toast.success(`Pull finished · matched ${p2.matched} · unmatched ${p2.unmatched}`);
          if (p3) toast.success(`Import finished · ${p3.created} new SKUs · ${p3.updated} updated`);
        }
      }, 1500);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    // eslint-disable-next-line
  }, [pushState.running, pullState.running, importState.running]);

  const doPull = async () => {
    setBusy("pull");
    try {
      await api.post("/bukku/products/pull");
      toast.info("Pull started · progress will appear below");
      await loadPullState();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pull failed");
    } finally { setBusy(null); }
  };
  const doPush = async () => {
    if (!window.confirm(`Push ${status.missing} local SKUs to Bukku? This will create new products in Bukku for each SKU that isn't already there.`)) return;
    setBusy("push");
    try {
      await api.post("/bukku/products/push");
      toast.info("Push started · progress will appear below");
      await loadPushState();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Push failed");
    } finally { setBusy(null); }
  };
  const doCancel = async () => {
    setBusy("cancel");
    try { await api.post("/bukku/products/push-cancel"); toast.info("Cancelling…"); }
    catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
    finally { setBusy(null); }
  };
  const doImport = async () => {
    if (!window.confirm(`Import all Bukku products into your local Master SKU sheet? Existing SKUs will be updated with the Bukku name and product-id link. New SKUs will be created for any Bukku product not already in your sheet.`)) return;
    setBusy("import");
    try {
      await api.post("/bukku/products/import-to-skus");
      toast.info("Import started · progress will appear below");
      await loadImportState();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(null); }
  };

  const pct = pushState.total > 0 ? Math.round((pushState.done / pushState.total) * 100) : 0;

  return (
    <div data-testid="bukku-products-page">
      <header className="mb-6 flex items-end justify-between">
        <div>
          <div className="caption">Accounting Sync</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Bukku Product Sync</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Match your CargoLedger SKUs to Bukku products. Pull first to link existing products by SKU code, then push the rest to create them automatically.
          </p>
        </div>
        <Button variant="outline" onClick={() => { loadStatus(); testConn(); loadPushState(); }} data-testid="bp-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </header>

      {/* Connection banner */}
      {conn && (
        <div className={`rounded-lg border p-3 mb-4 flex items-center gap-2 text-sm ${conn.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`} data-testid="bp-conn-banner">
          {conn.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {conn.ok
            ? <span>Connected to <b>{conn.companies?.[0]?.legal_name}</b> (<code>{conn.subdomain}.bukku.my</code>) · API <code>{conn.api}</code></span>
            : <span>Bukku not reachable · {typeof conn.error === "string" ? conn.error : JSON.stringify(conn.error)}</span>}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Stat label="Total Local SKUs" value={status.total_local_skus} />
        <Stat label="Synced to Bukku" value={status.synced_to_bukku} tone="green" />
        <Stat label="Missing in Bukku" value={status.missing} tone={status.missing > 0 ? "amber" : "green"} />
      </div>

      {/* Actions */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-3 flex-wrap">
          <Button onClick={doImport} disabled={busy === "import" || importState.running} data-testid="bp-btn-import">
            <DownloadCloud className="w-4 h-4 mr-1.5" />
            {importState.running ? "Importing…" : "Import All Bukku Products → Master SKU"}
          </Button>
          <Button onClick={doPull} disabled={busy === "pull" || pullState.running} variant="outline" data-testid="bp-btn-pull">
            <CloudDownload className="w-4 h-4 mr-1.5" />
            {pullState.running ? "Linking…" : "Link existing SKUs by code"}
          </Button>
          <Button onClick={doPush} disabled={busy === "push" || pushState.running || status.missing === 0} variant="secondary" data-testid="bp-btn-push">
            <CloudUpload className="w-4 h-4 mr-1.5" />
            {pushState.running ? "Pushing…" : `Push ${status.missing} Missing → Bukku`}
          </Button>
          {pushState.running && (
            <Button onClick={doCancel} variant="outline" data-testid="bp-btn-cancel">
              <StopCircle className="w-4 h-4 mr-1.5" /> Cancel push
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-3">
          <b>Import</b> copies every Bukku product's name + SKU into your local Master SKU sheet (upsert by SKU). <b>Link</b> updates existing local SKUs with their Bukku product-id. <b>Push</b> creates local SKUs that don't exist in Bukku. On invoice sync, products are matched by <b>name</b> first (how Bukku recalls items) then by SKU as fallback.
        </p>
      </div>

      {/* Import Progress */}
      {(importState.total > 0 || importState.running) && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 mb-6" data-testid="bp-import-progress">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-mono">
              Import: {importState.done} / {importState.total || "?"} · <span className="text-emerald-600">✓ {importState.created} new</span> · <span className="text-sky-600">↻ {importState.updated} updated</span> · <span className="text-slate-500">- {importState.skipped} skipped</span>
            </span>
            <span className="font-mono text-slate-500">{importState.total > 0 ? Math.round((importState.done / importState.total) * 100) : 0}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: `${importState.total > 0 ? Math.round((importState.done / importState.total) * 100) : 0}%` }} />
          </div>
          {importState.error && <div className="mt-2 text-xs text-rose-600">{importState.error}</div>}
        </div>
      )}

      {/* Pull Progress */}
      {(pullState.total > 0 || pullState.running) && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 mb-6" data-testid="bp-pull-progress">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-mono">
              Pull: {pullState.done} / {pullState.total || "?"} · <span className="text-emerald-600">✓ {pullState.matched} matched</span> · <span className="text-amber-600">{pullState.unmatched} unmatched</span>
            </span>
            <span className="font-mono text-slate-500">{pullState.total > 0 ? Math.round((pullState.done / pullState.total) * 100) : 0}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-sky-500 transition-all duration-300" style={{ width: `${pullState.total > 0 ? Math.round((pullState.done / pullState.total) * 100) : 0}%` }} />
          </div>
          {pullState.error && <div className="mt-2 text-xs text-rose-600">{pullState.error}</div>}
        </div>
      )}

      {/* Push Progress */}
      {(pushState.total > 0 || pushState.running) && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 mb-6" data-testid="bp-progress">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-mono">
              {pushState.done} / {pushState.total} · <span className="text-emerald-600">✓ {pushState.created}</span> · <span className="text-rose-600">✗ {pushState.failed}</span>
            </span>
            <span className="font-mono text-slate-500">{pct}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
          {pushState.errors?.length > 0 && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-rose-600">Show {pushState.errors.length} errors</summary>
              <div className="mt-2 max-h-64 overflow-auto border border-slate-100 rounded">
                <table className="excel-table w-full text-xs">
                  <thead><tr><th>SKU</th><th>Error</th></tr></thead>
                  <tbody>
                    {pushState.errors.slice(0, 100).map((e, i) => (
                      <tr key={i}><td className="font-mono">{e.sku}</td><td className="text-slate-600">{e.error}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}

      <div className="rounded-lg border border-dashed border-slate-200 p-4 text-xs text-slate-500 flex items-start gap-2">
        <Link2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <b>How this affects invoice sync:</b> once a local SKU has a linked Bukku product, invoice pushes will include that product on the bill line. For any SKU still missing when you push an invoice, it will be auto-created in Bukku on the fly.
        </div>
      </div>
    </div>
  );
}
